/**
 * Bank account schemes — how an account is identified in its country.
 * @module banking/schemes/bank-account-scheme
 */

import type { BankingValidationResult } from '../validation';

/** Notation of a bank account; picks its validator. */
export const BankAccountScheme = {
  /** Any IBAN country. */
  IBAN: 'IBAN',
  /** Czech domestic `[prefix-]number` with a 4-digit bank code. */
  CZ: 'CZ',
  /** Slovak domestic `[prefix-]number` with a 4-digit bank code. */
  SK: 'SK',
  /** UK sort code (6 digits) and account number (8 digits). */
  GB_SORT_CODE: 'GB_SORT_CODE',
  /** US ABA routing number (9 digits) and account number. */
  US_ABA: 'US_ABA',
  /** Any other country: bank identifier and account number without a format check. */
  OTHER: 'OTHER'
} as const;

export type BankAccountScheme = (typeof BankAccountScheme)[keyof typeof BankAccountScheme];

/** One account as written by its source. */
export interface BankAccount {
  scheme: BankAccountScheme;
  /** ISO 3166-1 alpha-2; required for `OTHER`, implied by the other schemes. */
  countryCode?: string | null;
  /** Required for `IBAN`, ignored by the domestic schemes. */
  iban?: string | null;
  /** CZ/SK bank code, GB sort code, US ABA routing number, … */
  bankIdentifier?: string | null;
  /** Domestic account number; CZ/SK may carry a `prefix-`. */
  accountNumber?: string | null;
  /** Optional in every scheme. */
  bic?: string | null;
}

/** An account in its canonical written form. */
export interface NormalizedBankAccount {
  scheme: BankAccountScheme;
  countryCode: string;
  iban: string | null;
  bankIdentifier: string | null;
  accountNumber: string | null;
  bic: string | null;
}

/** Validation, normalization and IBAN derivation of one scheme. */
export interface BankAccountSchemeDefinition {
  readonly scheme: BankAccountScheme;
  validate(account: BankAccount): BankingValidationResult;
  /** Null when the account cannot be read in this scheme's format. */
  normalize(account: BankAccount): NormalizedBankAccount | null;
  /** Null when the scheme has no IBAN or the account cannot be read. */
  toIban(account: BankAccount): string | null;
}

export function isBankAccountScheme(value: unknown): value is BankAccountScheme {
  return Object.values(BankAccountScheme).some(scheme => scheme === value);
}
